import { useState, useEffect, useContext, useRef } from "react";
import axios from "axios";

import { DataContext } from "../../utils/DataContext";
import { UserAuth } from "../../utils/AuthContext";
import { useTranslation } from "react-i18next";
import useLanguage from "../../utils/useLanguage";
import { useLocation, useNavigate } from "react-router-dom";

import PDFUploader from "../../components/PDFUploader";
import BackToDashboard from "../../components/BackToDashboard";
import ErrorBox from "../../components/ErrorBox";
import Loader from "../../components/Loader";
import CustomInput from "../../components/CustomInput";
import Dropdown from "../../components/Dropdown";
import CustomButton from "../../components/CustomButton";
import SectionDivider from "../../components/SectionDivider";
import previousIcon from "../../assets/previous_icon.png";
import nextIcon from "../../assets/next_icon.png";
import addIcon from "../../assets/add_icon.png";

import Modal from "../../components/Modal";
import classes from "../../components/Modal.module.css";
import saveIcon from "../../assets/save_icon.png";
import closeIcon from "../../assets/close_icon.png";
import { sanitizeNumber } from "../../utils/sanitizeNumber";
import { formatDate } from "../../utils/toDate";
import InformationBox from "../../components/InformationBox";
import { FailedIcon, DoneIcon } from "../../assets/registtyIcons/icons";
import HotDropdown from "../../components/HotDropdown";
import { onlyNums } from "../../utils/OnlyNums";
import AddAnotherButton, { GENDER } from "../../components/AddAnotherButton";
import PageWrapper from "../../components/PageWrapper";

const AnalyzedInvoice = ({ setShowInputFields }) => {
  const { t } = useTranslation("general");
  const { currentDirection } = useLanguage();
  const navigate = useNavigate();
  const containerDiv = useRef(null);

  const location = useLocation();
  const extractedData = location.state?.extractedData;

  const [analyzeLoading, setAnalyzeLoading] = useState(false);
  const pdfFile = location.state?.pdfFile;
  // const [extractedData, setExtractedData] = useState({});

  const [supplierWebsite, setSupplierWebsite] = useState({
    URL: extractedData?.websiteURL,
    urlStatus: extractedData?.urlStatus,
  });

  const [invoice, setInvoice] = useState({
    invoiceNumber: extractedData?.["Invoice Number"],
    amountForSupplier: parseInt(extractedData?.totalInvoiceAmount || 0),
    abstractAmountForCustomer: "",
    finalAmountForCustomer: "",
    date: extractedData?.["Invoice Date"]
      ? extractedData["Invoice Date"]
      : new Date(),

    received: "",
    left: "",
    notes: "",
  });

  useEffect(() => {
    const handleUpload = async (event) => {
      if (event) event.preventDefault();

      if (!pdfFile) {
        navigate("/");
        return;
      }

      const formData = new FormData();
      formData.append("file", pdfFile);

      try {
        const uploadResponse = await axios.post(
          `${process.env.REACT_APP_URL}/extractPdf`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${userData?.token}`,
              "User-Id": userData?.userId,
              "Supplier-Id": selectedSupplier?.id,
            },
          }
        );

        if (uploadResponse.status === 200) {
          // setExtractedData(uploadResponse.data);

          setInvoice({
            invoiceNumber: uploadResponse?.data?.["Invoice Number"],
            amountForSupplier: uploadResponse?.data?.totalInvoiceAmount,
            abstractAmountForCustomer:
              uploadResponse?.data?.abstractAmountForCustomer,
            finalAmountForCustomer:
              uploadResponse?.data?.finalAmountForCustomer,
            date:
              new Date(uploadResponse?.data?.["Invoice Date"]) || new Date(),

            received: uploadResponse?.data?.received,
            left: uploadResponse?.data?.left,
            notes: uploadResponse?.data?.notes,
          });
        }
      } catch (error) {
        console.error("Upload failed:", error);
      } finally {
        setAnalyzeLoading(false);
      }
    };

    // handleUpload();
  }, []);

  const containers = extractedData?.Containers?.filter(
    (container) =>
      typeof container.containerNumber === "string" &&
      container.containerNumber.trim() !== ""
  )?.map((container) => ({
    amountForSupplier: parseFloat(container.totalValue || 0),
    containerNumber: container.containerNumber,
    material: container.material,
    numberOfBoxes: container.quantity,
    unitPrice: container.unitPrice,
    expectedArrivalDate: new Date(),
    permitsReceivingDate: new Date(),
    dateOfEntry: new Date(),
    shippingDate: new Date(),
    exitDate: new Date(),
  }));

  // * Only containers data
  const [inputs, setInputs] = useState(containers || []);

  const {
    suppliers,
    getSuppliers,
    selectedSupplier,
    error,
    setError,
    shippedContainers,
    setShippedContainers,
    selectedCustomer,
    customers,
    handleSelectSupplier,
    handleSelectCustomer,
    subCompanies,
    selectedSubCompany,
    handleSelectSubCompany,
    getSubCompanies,
    getCustomers,
  } = useContext(DataContext);

  const { userData } = useContext(UserAuth);

  const [invoiceDocs, setInvoiceDocs] = useState([]);
  const pdfUploaderRef = useRef(null);

  const [loadingModal, setLoadingModal] = useState(false);

  const [page, setPage] = useState(1);

  const handleAddInput = (e) => {
    setTimeout(() => {
      containerDiv.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
    const newContainer = {
      containerNumber: "",
      amountForSupplier: "",
      expectedArrivalDate: new Date(),
      finalDestination: "",
      material: "",
      unitPrice: "",
      numberOfBoxes: 0,
      measuringUnit: "",
      shipper: "",
      shippingDate: new Date(),
      exitDate: new Date(),
      abstractAmountForCustomer: "",
      finalAmountForCustomer: "",
      dateOfEntry: new Date(),
      howWasPapersReceived: "",
      permitsReceivingDate: new Date(),
      notes: "",
    };

    setInputs((prevInputs) => {
      const updatedInputs = [...prevInputs, newContainer];
      extractedData.Containers = [
        ...extractedData.Containers,
        { container: newContainer },
      ];
      setPage(prevInputs?.length + 1);
      return updatedInputs;
    });
  };

  const handleInputChange = (e, pageIndex) => {
    const { name, value } = e.target;

    setInputs((prevInputs) => {
      return prevInputs.map((item, index) =>
        index === pageIndex ? { ...item, [name]: value } : item
      );
    });
  };

  const [received, setReceived] = useState(extractedData?.totalInvoiceAmount);

  const handleNext = () => {
    if (page < inputs.length) setPage(page + 1);
    setTimeout(() => {
      containerDiv.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  const handlePrevious = () => {
    if (page > 1) setPage(page - 1);
    setTimeout(() => {
      containerDiv.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  const handleDateChange = (e) => {
    const date = e.target.value;
    const name = e.target?.name;

    const updatedInputs = [...inputs];
    updatedInputs[page - 1][name] = new Date(date);
    setInputs(updatedInputs);
  };

  const handleAddRow = async () => {
    try {
      const token = userData?.token;
      const userId = parseInt(userData?.userId, 10);
      const organizationId = parseInt(userData?.organizationId, 10);

      if (!userId) {
        console.error("User ID not found in localStorage");
        return;
      }

      if (!invoice?.invoiceNumber) {
        setError("invalidInvoiceNumber");
        window.scroll(0, 0);
        return;
      }

      if (!selectedSupplier?.id) {
        setError("noSupplier");
        window.scroll(0, 0);
        return;
      }

      if (!selectedCustomer?.id) {
        setError("noCustomer");
        window.scroll(0, 0);
        return;
      }

      if (!invoice?.abstractAmountForCustomer) {
        setError("noAbstractAmountForCustomer");
        window.scroll(0, 0);
        return;
      }

      setLoadingModal(true);
      setError("");

      const payload = {
        supplierId: selectedSupplier?.id,
        customerId: selectedCustomer?.id,
        subCompanyId: selectedSubCompany?.id,
        createdBy: userId,
        received: sanitizeNumber(received),
        left:
          sanitizeNumber(received) -
          inputs.reduce(
            (acc, curr) => acc + parseFloat(curr.amountForSupplier || 0),
            0
          ),
        ...invoice,
        // containers: inputs?.filter((input) => input?.containerNumber),
        containers: inputs?.filter((input) => input?.containerNumber),
        organizationId,
      };

      setError("");
      const response = await axios.post(
        `${process.env.REACT_APP_URL}/createMultiContainersInvoice`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "User-Id": userId,
            "Supplier-Id": selectedSupplier?.id,
          },
        }
      );

      if (response.status === 200) {
        setShippedContainers(response.data.containers);
        handleChildUploadDocs(response?.data?.invoice?.id);
        navigate("/invoicesOfShippedContainers");
        setError("");
      }
    } catch (error) {
      const errMsg = error.response?.data?.message;
      if (errMsg === "alreadyLinkedToAnInvoice") {
        const containerNumber = error?.response?.data?.containerNumber;
        setError(`${t("container")} ${containerNumber} ${t(errMsg)}`);
      } else {
        setError(errMsg || "An error occurred");
      }
      window.scroll(0, 0);
    } finally {
      setLoadingModal(false);
    }
  };

  const handleAmountChange = (e) => {
    const { name, value } = e.target;

    const parsedValue = value || 0;

    setInvoice((prev) => {
      // Extract all relevant numbers from the previous state
      const {
        amountForSupplier,
        abstractAmountForCustomer,
        currencyRate,
        received,
      } = prev;

      let updatedValues = { [name]: parsedValue };

      // Define dependencies and update accordingly
      if (name === "amountForSupplier") {
        updatedValues.abstractAmountForCustomer =
          parsedValue * (currencyRate || 1);
        updatedValues.received = 0;
        updatedValues.left = 0;
      } else if (name === "currencyRate") {
        updatedValues.abstractAmountForCustomer =
          (amountForSupplier || 0) * parsedValue;
        updatedValues.received = 0;
      }
      // If the user type that value manually, empty the currencyRate value
      else if (name === "abstractAmountForCustomer") {
        updatedValues.currencyRate = 0;
        updatedValues.received = 0;
      }

      // Prevent received from exceeding abstractAmountForCustomer
      if (name === "received") {
        updatedValues.received = parsedValue;
        updatedValues.left = abstractAmountForCustomer - parsedValue || 0;
      } else {
        updatedValues.left =
          updatedValues.abstractAmountForCustomer ??
          Math.min(
            abstractAmountForCustomer - received,
            abstractAmountForCustomer
          );
      }

      return {
        ...prev,
        ...updatedValues,
      };
    });
  };

  const handleCancel = () => {
    // setShowInputFields(false);
    setInputs([
      {
        containerNumber: "",
        expectedArrivalDate: new Date(),
        finalDestination: "",
        shipper: "",
        material: "",
        numberOfBoxes: 0,
        measuringUnit: "",
        exitDate: new Date(),
        dateOfEntry: new Date(),
        shippingDate: new Date(),
        permitsReceivingDate: new Date(),
        amountForSupplier: "",
        abstractAmountForCustomer: "",
        finalAmountForCustomer: "",
        notes: "",
      },
    ]);
    navigate("/invoicesOfShippedContainers");
  };

  const handleInvoiceInputChange = (e) => {
    const { name, value } = e.target;

    setInvoice((prev) => ({ ...prev, [name]: value }));
  };

  const handleInvoiceDateChange = (e) => {
    const { name, value } = e.target;

    setInvoice((prev) => ({ ...prev, [name]: new Date(value) }));
  };

  const handleChildUploadDocs = (multiContainersInvoiceId) => {
    if (pdfUploaderRef.current) {
      pdfUploaderRef.current.handleUploadDocs(multiContainersInvoiceId);
    }
  };

  const handleDeleteContainer = (e) => {
    const containerIndex = parseInt(
      e?.target?.getAttribute("data-container-index")
    );

    const filteredInputs = inputs?.filter(
      (input, index) => index !== containerIndex
    );

    setInputs(filteredInputs);
    handlePrevious();
  };

  return (
    <PageWrapper>
      <div className="add-shipped-container-page">
        <div className="add-shipped-container-top-actions">
          <div className="user-logout">
            <BackToDashboard
              title={t("back")}
              variant="blank"
              icon={currentDirection === "ltr" ? previousIcon : nextIcon}
              iconSize={14}
            />
          </div>
        </div>
        <h3 className={"add-shipped-container-title"}>{t("createInvoice")}</h3>
        {loadingModal ? <Loader /> : null}
        {analyzeLoading && <Loader />}
        {error && !loadingModal && (
          <div className="u-width_fit-content u-margin-center">
            <ErrorBox>{t(error)}</ErrorBox>
          </div>
        )}
        <div className="add-shipped-container-invoice-part">
          {!selectedSupplier?.id ||
          selectedSupplier?.id === "All" ||
          !selectedCustomer?.id ||
          selectedCustomer?.id === "All" ? (
            <InformationBox>
              {t("youHaveToSelectSupplierAndCustomer")}
            </InformationBox>
          ) : null}
          <div className="add-shipped-container-header">
            <section className="add-shipped-container-header-section">
              <span>{t("supplier")}: </span>

              <HotDropdown
                initOptions={[{ id: "All", name: "All" }, ...suppliers]}
                selected={
                  selectedSupplier.id && selectedSupplier.id !== "All"
                    ? selectedSupplier
                    : { id: "", name: `--${t("chooseASupplier")}--` }
                }
                setSelected={(supplier) =>
                  handleSelectSupplier(supplier, "noRequest")
                }
                flex
                // page="noRequest"
                getData={getSuppliers}
                errorText={error}
                optionsName={"suppliers"}
              />
            </section>
            <section className="add-shipped-container-header-section">
              <span>{t("customer")}: </span>

              <div className={classes.invoiceNumberContainer}>
                <HotDropdown
                  initOptions={[{ id: "All", name: "All" }, ...customers]}
                  selected={
                    selectedCustomer.id && selectedCustomer.id !== "All"
                      ? selectedCustomer
                      : { id: "", name: `--${t("chooseACustomer")}--` }
                  }
                  getData={getCustomers}
                  // page="noRequest"
                  setSelected={handleSelectCustomer}
                  flex={true}
                  errorText={error}
                  optionsName={"customers"}
                />
              </div>
            </section>
          </div>

          {/* Website details */}
          <div className="o-column o-column--gap-size_300 u-flex_flex-1 u-padding--top_300">
            <SectionDivider title="websiteDetails" />

            <section className="o-row">
              <span className="min-width-150">{t("website")}: </span>
              {supplierWebsite?.URL ? (
                <a
                  href={
                    supplierWebsite?.URL &&
                    supplierWebsite.URL.startsWith("http")
                      ? supplierWebsite.URL
                      : `http://${supplierWebsite?.URL}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link-color t-bold_bold"
                >
                  {supplierWebsite?.URL}
                </a>
              ) : (
                <span className="not-working-website">
                  {t(supplierWebsite?.urlStatus)}
                </span>
              )}
            </section>
            <section className="o-row">
              <span className="min-width-150">{t("websiteStatus")}: </span>

              {supplierWebsite?.urlStatus !== "noURLProvided" ? (
                <div className="o-row o-row--vertical-align_center o-row--gap-size_100">
                  {/* Status Icon */}
                  {supplierWebsite?.urlStatus === "working" ? (
                    <DoneIcon width={20} height={20} />
                  ) : supplierWebsite?.urlStatus === "notWorking" ||
                    supplierWebsite?.urlStatus === "notExist" ? (
                    <FailedIcon width={16} height={16} />
                  ) : null}

                  {/* Status text */}
                  <span
                    className={
                      supplierWebsite?.urlStatus === "working"
                        ? "working-website"
                        : "not-working-website"
                    }
                  >
                    {t(supplierWebsite?.urlStatus)}
                  </span>
                </div>
              ) : null}
            </section>
          </div>

          {/* Invoice details */}
          <div
            className={`o-column o-column--gap-size_300 u-flex_flex-1 u-padding--top_300 ${
              analyzeLoading ? "disabled-area" : ""
            }`}
          >
            <SectionDivider title="invoiceDetails" />
            <section>
              <div className={classes.inputContainer}>
                <span>{t("subCompany")}: </span>
                <Dropdown
                  options={subCompanies}
                  selected={selectedSubCompany}
                  setSelected={handleSelectSubCompany}
                  flex={true}
                  getData={getSubCompanies}
                  nameInAr={true}
                />
              </div>
            </section>
            <section>
              <div className={classes.inputContainer}>
                <span>{t("invoiceNumber")}: </span>
                <div className={classes.invoiceNumberContainer}>
                  <CustomInput
                    name="invoiceNumber"
                    value={invoice?.invoiceNumber}
                    onChange={handleInvoiceInputChange}
                    flex={true}
                  />
                </div>
              </div>
            </section>
            <section>
              <div className={classes.inputContainer}>
                <span>{t("Date")}: </span>
                <div className={classes.invoiceNumberContainer}>
                  <CustomInput
                    name="date"
                    type="date"
                    value={formatDate(invoice?.date)}
                    onChange={handleInvoiceDateChange}
                    flex={true}
                  />
                </div>
              </div>
            </section>
            <section>
              <div className={classes.inputContainer}>
                <span>{t("amountForSupplier")}: </span>
                <div className="o-row o-row--vertical-align_center o-row--gap-size_300 u-width_full-width">
                  <CustomInput
                    name="amountForSupplier"
                    value={invoice?.amountForSupplier || 0}
                    onChange={handleAmountChange}
                    flex={true}
                    type="moneyV2"
                  />
                  <div className="t-bold_bold">
                    {selectedSupplier?.currency}
                  </div>
                </div>
              </div>
            </section>
            <section className={classes.section}>
              <div className={classes.inputContainer}>
                <span>{t("currencyRate")}: </span>
                <div
                  className={`o-row o-row--horizontal-align_space-between o-row--gap-size_400 o-row--vertical-align_center u-width_full-width`}
                >
                  <div className={classes.invoiceNumberContainer}>
                    <CustomInput
                      name="currencyRate"
                      value={invoice?.currencyRate}
                      onChange={handleAmountChange}
                      type="moneyV2"
                      flex={true}
                    />
                  </div>
                  <div>{selectedCustomer?.currency}</div>
                </div>
              </div>
            </section>
            <section>
              <div className={classes.inputContainer}>
                <span>{t("abstractAmountForCustomer")}: </span>
                <div className="o-row o-row--vertical-align_center o-row--gap-size_300 u-width_full-width">
                  <CustomInput
                    name="abstractAmountForCustomer"
                    value={invoice?.abstractAmountForCustomer}
                    onChange={handleAmountChange}
                    flex={true}
                  />
                  <div className="t-bold_bold">
                    {selectedCustomer?.currency}
                  </div>
                </div>
              </div>
            </section>
            {/* <section>
            <div className={classes.inputContainer}>
              <span>{t("finalAmountForCustomer")}: </span>
              <div className="o-row o-row--vertical-align_center o-row--gap-size_300 u-width_full-width">
                <CustomInput
                  name="finalAmountForCustomer"
                  value={invoice?.finalAmountForCustomer}
                  onChange={handleInvoiceInputChange}
                  flex={true}
                />
                <div className="t-bold_bold">{selectedCustomer?.currency}</div>
              </div>
            </div>
          </section> */}
            <section>
              <div className={classes.inputContainer}>
                <span>{t("received")}: </span>
                <div className="o-row o-row--vertical-align_center o-row--gap-size_300 u-width_full-width">
                  <CustomInput
                    name="received"
                    value={invoice?.received}
                    onChange={handleAmountChange}
                    flex={true}
                    type="moneyV2"
                  />
                  <div className="t-bold_bold">
                    {selectedCustomer?.currency}
                  </div>
                </div>
              </div>
            </section>
            <section>
              <div className={classes.inputContainer}>
                <span>{t("left")}: </span>
                <div className="o-row o-row--vertical-align_center o-row--gap-size_300 u-width_full-width">
                  <CustomInput
                    name="left"
                    value={invoice?.left}
                    onChange={handleAmountChange}
                    flex={true}
                    type="moneyV2"
                  />
                  <div className="t-bold_bold">
                    {selectedCustomer?.currency}
                  </div>
                </div>
              </div>
            </section>

            <section>
              <div className={classes.inputContainer}>
                <span>{t("notes")}: </span>
                <div className="o-row o-row--vertical-align_center o-row--gap-size_300 u-width_full-width">
                  <CustomInput
                    name="notes"
                    value={invoice?.notes}
                    onChange={handleInvoiceInputChange}
                    flex={true}
                    type="textarea"
                  />
                  <div className="t-bold_bold">
                    {selectedCustomer?.currency}
                  </div>
                </div>
              </div>
            </section>
            <PDFUploader
              ref={pdfUploaderRef}
              invoiceNumber={shippedContainers?.invoiceNumber}
              invoiceDocs={invoiceDocs}
              setInvoiceDocs={setInvoiceDocs}
              mode="upload"
              documents={shippedContainers?.documents}
              tempDocs={invoiceDocs}
              setTempDocs={setInvoiceDocs}
              invoiceType="multiContainersInvoice"
            />
          </div>

          {/* Containers details */}
        </div>

        <div
          className={`o-column  u-flex_flex-1 u-padding--top_300 ${
            analyzeLoading ? "disabled-area" : ""
          }`}
        >
          {inputs?.length ? (
            <>
              <SectionDivider title="containersDetails" />

              <div
                className={`add-shipped-container-container-part ${classes.flexableForm} ${classes.flexableFormWithPadding}`}
                ref={containerDiv}
              >
                {inputs?.map((item, index) => {
                  if (index === page - 1) {
                    return (
                      <>
                        <section
                          className={`${classes.section} ${classes.invoiceNumber}`}
                        >
                          <div className={classes.inputContainer}>
                            <span>{t("customer")}: </span>
                            <div className={classes.invoiceNumberContainer}>
                              <HotDropdown
                                initOptions={[
                                  { id: "All", name: "All" },
                                  ...customers,
                                ]}
                                selected={
                                  selectedCustomer.id &&
                                  selectedCustomer.id !== "All"
                                    ? selectedCustomer
                                    : {
                                        id: "",
                                        name: `--${t("chooseACustomer")}--`,
                                      }
                                }
                                getData={getCustomers}
                                // page="noRequest"
                                setSelected={handleSelectCustomer}
                                flex={true}
                                errorText={error}
                                optionsName={"customers"}
                              />
                            </div>
                          </div>
                        </section>
                        <section
                          className={`${classes.section} ${classes.invoiceNumber}`}
                        >
                          <div className={classes.inputContainer}>
                            <span>{t("containerNumber")}: </span>
                            <div className={classes.invoiceNumberContainer}>
                              <CustomInput
                                name="containerNumber"
                                value={item?.containerNumber || ""}
                                onChange={(e) => handleInputChange(e, page - 1)}
                                flex={true}
                              />
                            </div>
                          </div>
                        </section>
                        {/* <section>
                        <div className={classes.inputContainer}>
                          <span>{t("Date")}: </span>
                          <CustomInput
                            name="date"
                            type="date"
                            flex={true}
                            value={formatDate(invoice?.date)}
                            onChange={(e) => handleInvoiceDateChange(e)}
                          />
                        </div>
                      </section> */}

                        <section className={classes.section}>
                          <div className={classes.inputContainer}>
                            <span>{t("amountForSupplier")}: </span>
                            <div className="o-row o-row--vertical-align_center o-row--gap-size_300 u-width_full-width">
                              <CustomInput
                                name="amountForSupplier"
                                value={item?.amountForSupplier || 0}
                                onChange={(e) => handleInputChange(e, page - 1)}
                                type="moneyV2"
                                flex={true}
                              />
                              <div className="t-bold_bold">
                                {selectedSupplier?.currency}
                              </div>
                            </div>
                          </div>
                        </section>

                        <section className={classes.section}>
                          <div className={classes.inputContainer}>
                            <span>{t("expectedArrivalDate")}: </span>
                            <CustomInput
                              name="expectedArrivalDate"
                              type="date"
                              flex={true}
                              value={formatDate(item?.expectedArrivalDate)}
                              onChange={handleDateChange}
                            />
                          </div>
                        </section>

                        <section
                          className={`${classes.section} ${classes.invoiceNumber}`}
                        >
                          <div className={classes.inputContainer}>
                            <span>{t("finalDestination")}: </span>
                            <CustomInput
                              name="finalDestination"
                              value={item?.finalDestination}
                              onChange={(e) => handleInputChange(e, page - 1)}
                              flex={true}
                            />
                          </div>
                        </section>

                        <section
                          className={`${classes.section} ${classes.invoiceNumber}`}
                        >
                          <div className={classes.inputContainer}>
                            <span>{t("material")}: </span>
                            <CustomInput
                              name="material"
                              value={item?.material}
                              onChange={(e) => handleInputChange(e, page - 1)}
                              flex={true}
                            />
                          </div>
                        </section>

                        <section
                          className={`${classes.section} ${classes.invoiceNumber}`}
                        >
                          <div className={classes.inputContainer}>
                            <span>{t("unitPrice")}: </span>
                            <div className="o-row o-row--vertical-align_center o-row--gap-size_300 u-width_full-width">
                              <CustomInput
                                name="unitPrice"
                                value={item?.unitPrice}
                                onChange={(e) => handleInputChange(e, page - 1)}
                                flex={true}
                                type="moneyV2"
                              />
                              <div className="t-bold_bold">
                                {selectedSupplier?.currency}
                              </div>
                            </div>
                          </div>
                        </section>

                        <section
                          className={`${classes.section} ${classes.invoiceNumber}`}
                        >
                          <div className={classes.inputContainer}>
                            <span>{t("numberOfBoxes")}: </span>
                            <CustomInput
                              name="numberOfBoxes"
                              value={item?.numberOfBoxes}
                              onChange={(e) => handleInputChange(e, page - 1)}
                              type="moneyV2"
                              flex={true}
                            />
                          </div>
                        </section>
                        <section
                          className={`${classes.section} ${classes.invoiceNumber}`}
                        >
                          <div className={classes.inputContainer}>
                            <span>{t("measuringUnit")}</span>
                            <CustomInput
                              name="measuringUnit"
                              value={item?.measuringUnit}
                              onChange={(e) => handleInputChange(e, page - 1)}
                              flex={true}
                            />
                          </div>
                        </section>

                        <section
                          className={`${classes.section} ${classes.invoiceNumber}`}
                        >
                          <div className={classes.inputContainer}>
                            <span>{t("shipper")}: </span>
                            <CustomInput
                              name="shipper"
                              value={item?.shipper}
                              onChange={(e) => handleInputChange(e, page - 1)}
                              flex={true}
                            />
                          </div>
                        </section>

                        {/* Date of Entry */}
                        <section className={classes.section}>
                          <div className={classes.inputContainer}>
                            <span>{t("dateOfEntry")}: </span>
                            <CustomInput
                              name="dateOfEntry"
                              type="date"
                              flex={true}
                              value={formatDate(item?.dateOfEntry)}
                              onChange={handleDateChange}
                            />
                          </div>
                        </section>

                        {/* How Was Papers Received */}
                        <section className={classes.section}>
                          <div className={classes.inputContainer}>
                            <span>{t("howWasPapersReceived")}: </span>
                            <Dropdown
                              options={[
                                {
                                  title: "receivedByUs",
                                  value: "receivedByUs",
                                },
                                {
                                  title: "receivedByCustomer",
                                  value: "receivedByCustomer",
                                },
                              ]}
                              selected={item?.howWasPapersReceived || {}}
                              setSelected={(status) =>
                                handleInputChange(
                                  {
                                    target: {
                                      name: "howWasPapersReceived",
                                      value: status,
                                    },
                                  },
                                  page - 1
                                )
                              }
                              flex
                            />
                          </div>
                        </section>
                        {/* Shipping Date */}
                        <section className={classes.section}>
                          <div className={classes.inputContainer}>
                            <span>{t("shippingDate")}: </span>
                            <CustomInput
                              name="shippingDate"
                              type="date"
                              flex={true}
                              value={formatDate(item?.shippingDate)}
                              onChange={handleDateChange}
                            />
                          </div>
                        </section>

                        {/* Exit Date */}
                        <section className={classes.section}>
                          <div className={classes.inputContainer}>
                            <span>{t("exitDate")}: </span>
                            <CustomInput
                              name="exitDate"
                              type="date"
                              flex={true}
                              value={formatDate(item?.exitDate)}
                              onChange={handleDateChange}
                            />
                          </div>
                        </section>

                        {/* Abstract Amount For Customer */}
                        <section className={classes.section}>
                          <div className={classes.inputContainer}>
                            <span>{t("abstractAmountForCustomer")}: </span>
                            <div className="o-row o-row--vertical-align_center o-row--gap-size_300 u-width_full-width">
                              <CustomInput
                                name="abstractAmountForCustomer"
                                value={item?.abstractAmountForCustomer}
                                onChange={(e) => handleInputChange(e, page - 1)}
                                onKeyDown={(e) => onlyNums(e)}
                                type="moneyV2"
                                flex={true}
                              />
                              <div className="t-bold_bold">
                                {selectedCustomer?.currency}
                              </div>
                            </div>
                          </div>
                        </section>

                        {/* Final Amount For Customer */}
                        <section className={classes.section}>
                          <div className={classes.inputContainer}>
                            <span>{t("finalAmountForCustomer")}: </span>
                            <div className="o-row o-row--vertical-align_center o-row--gap-size_300 u-width_full-width">
                              <CustomInput
                                name="finalAmountForCustomer"
                                value={item?.finalAmountForCustomer}
                                onChange={(e) => handleInputChange(e, page - 1)}
                                onKeyDown={(e) => onlyNums(e)}
                                type="moneyV2"
                                flex={true}
                              />
                              <div className="t-bold_bold">
                                {selectedCustomer?.currency}
                              </div>
                            </div>
                          </div>
                        </section>

                        {/* Permits Receiving Date */}
                        <section className={classes.section}>
                          <div className={classes.inputContainer}>
                            <span>{t("permitsReceivingDate")}: </span>
                            <CustomInput
                              name="permitsReceivingDate"
                              type="date"
                              flex={true}
                              value={formatDate(item?.permitsReceivingDate)}
                              onChange={handleDateChange}
                            />
                          </div>
                        </section>

                        {/* Notes */}
                        <section className={classes.section}>
                          <div className={classes.inputContainer}>
                            <span className={classes.notesClass}>
                              {t("notes")}:{" "}
                            </span>
                            <CustomInput
                              name="notes"
                              type="textarea"
                              value={item?.notes || ""}
                              onChange={(e) => handleInputChange(e, page - 1)}
                              flex={true}
                            />
                          </div>
                        </section>

                        <div className="o-row o-row--horizontal-align_end u-margin--top_500">
                          <button
                            onClick={handleDeleteContainer}
                            data-container-index={index}
                            className="temp-delete-button"
                          >
                            ❌ {t("delete")}
                          </button>
                        </div>
                      </>
                    );
                  }
                  return null;
                })}
              </div>
            </>
          ) : null}
        </div>
        <div>
          <div className={""}>
            {inputs?.length ? (
              <div className={"add-shipped-container-next-previous"}>
                <CustomButton
                  variant="blank"
                  title={t("previous")}
                  onClick={handlePrevious}
                  disabled={page === 1}
                  icon={currentDirection === "ltr" ? previousIcon : nextIcon}
                  iconSize={15}
                />
                <CustomButton
                  title={t("next")}
                  variant="blank"
                  onClick={handleNext}
                  disabled={page === inputs.length}
                  icon={currentDirection === "ltr" ? nextIcon : previousIcon}
                  iconSize={15}
                  reverseIcon
                />
              </div>
            ) : null}
          </div>
          <div
            className={`add-shipped-container-add-another ${
              analyzeLoading ? "disabled-area" : ""
            }`}
          >
            <AddAnotherButton
              onClick={handleAddInput}
              variant="blank"
              title={t("addContainer")}
              icon={addIcon}
              iconSize={16}
              gender={GENDER.FEMALE}
              arrayLength={inputs?.length}
              disabled={loadingModal}
            />
          </div>
        </div>

        <div>
          <section className={classes.actions}>
            <CustomButton
              onClick={handleAddRow}
              title={t("save")}
              icon={saveIcon}
              iconSize={16}
              disabled={
                loadingModal ||
                !selectedSupplier?.id ||
                selectedSupplier?.id === "All" ||
                !selectedCustomer?.id ||
                selectedCustomer?.id === "All"
              }
            />
            <CustomButton
              title={t("cancel")}
              onClick={handleCancel}
              variant="blank"
              icon={closeIcon}
              iconSize={14}
            />
          </section>
        </div>
      </div>
    </PageWrapper>
  );
};

export default AnalyzedInvoice;
